import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Container, CssBaseline, ThemeProvider, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import theme, { TEXT_COLOR } from '../../theme';
import Topbar from '../Topbar';
import Card from '../Dashboard/Card';
import CreditsHeader from './CreditsHeader';
import { creditsTokens as t } from './tokens';
import { withLanguage } from '../common/Texts';
import { toTitleCase } from '../../utils/toTitleCase';
import { API } from '../../api';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function Credits() {
    const company = useSelector((s) => s.user.company);
    const language = useSelector((s) => s.user.language);
    const tr = useMemo(() => withLanguage(language), [language]);

    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    // Coincide con el orden por defecto del backend (Meta.ordering del modelo).
    const [sortModel, setSortModel] = useState([{ field: 'created_date', sort: 'desc' }]);

    // El ledger crece con cada encuesta, así que la paginación y el orden son
    // del servidor: cada cambio dispara un fetch nuevo. El ref evita repetir el
    // mismo pedido, igual que en Results.
    const requestedKey = useRef(null);
    useEffect(() => {
        if (!company?.id) return undefined;

        const { page, pageSize } = paginationModel;
        const { field, sort } = sortModel[0] ?? {};
        const ordering = field ? `${sort === 'desc' ? '-' : ''}${field}` : '';
        const key = `${company.id}|${page}|${pageSize}|${ordering}`;
        if (requestedKey.current === key) return undefined;

        const controller = new AbortController();
        const params = new URLSearchParams({
            page: String(page + 1),
            page_size: String(pageSize),
        });
        if (ordering) params.set('ordering', ordering);

        setLoading(true);
        API.get(`/credits/?${params.toString()}`, { signal: controller.signal })
            .then(({ data }) => {
                // La clave se marca sólo al recibir la respuesta: si se marcara
                // antes, un pedido abortado (el doble montaje de StrictMode, o
                // un re-render con la misma clave) bloquearía el reintento y la
                // pantalla quedaría cargando para siempre.
                requestedKey.current = key;
                setRows(data?.results ?? []);
                setRowCount(data?.count ?? 0);
            })
            .catch((e) => {
                if (e?.name === 'AbortError') return;
                console.error('Error en Api:', e);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [company?.id, paginationModel, sortModel]);

    // Cambiar el orden reordena todo el ledger, no la página actual: quedarse
    // en la página 7 del orden anterior no significa nada.
    const handleSortModelChange = (model) => {
        setSortModel(model);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const columns = useMemo(() => [
        {
            field: 'created_date',
            headerName: tr('date'),
            width: 120,
            type: 'date',
            valueGetter: (value) => (value ? dayjs(value).toDate() : null),
            valueFormatter: (value) => (value ? dayjs(value).format('D/M/YYYY') : ''),
        },
        {
            field: 'delta',
            headerName: tr('movement'),
            width: 130,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => (
                <Typography component="span" sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: params.value > 0 ? t.color.credit : t.color.debit,
                }}>
                    {params.value > 0 ? `+${params.value}` : params.value}
                </Typography>
            ),
        },
        {
            field: 'reason',
            headerName: tr('reason'),
            flex: 1,
            minWidth: 170,
            // No se ordena: el backend ordenaría por la clave y el usuario ve
            // la etiqueta traducida, así que el orden no coincidiría.
            sortable: false,
            // El backend manda la clave estable; la etiqueta la pone el front
            // para que respete el idioma del usuario.
            valueGetter: (value) => tr(`credit_reason_${value}`) ?? value,
        },
        {
            field: 'actor_label',
            headerName: tr('triggered_by'),
            flex: 1,
            minWidth: 150,
            // Ídem `reason`: la celda muestra el nombre del candidato cuando lo
            // hay, no el `actor_label` que ordenaría el backend.
            sortable: false,
            // En una encuesta nueva el actor es el candidato anónimo: quien la
            // disparó de verdad es la persona que la contestó.
            valueGetter: (value, row) => toTitleCase(
                (row.reason === 'survey_started' && row.reply_name) || value,
            ),
        },
        {
            field: 'balance_after',
            headerName: tr('resulting_balance'),
            width: 150,
            align: 'right',
            headerAlign: 'right',
        },
    ], [tr]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <Box sx={{ minHeight: '100vh', bgcolor: t.color.bgSub }}>
                <Topbar/>
                <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
                    <CreditsHeader
                        language={language}
                        balance={company?.credits}
                        count={rowCount}
                    />

                    <Card padded={false} sx={{ overflow: 'hidden' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            autoHeight
                            disableColumnMenu
                            disableRowSelectionOnClick
                            paginationMode="server"
                            rowCount={rowCount}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            sortingMode="server"
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            localeText={{
                                MuiTablePagination: {
                                    labelRowsPerPage: tr('rows_per_page'),
                                },
                                noRowsLabel: tr('no_movements'),
                            }}
                            sx={{
                                border: 'none',
                                fontFamily: t.font.sans,
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: `1px solid ${t.color.divider}`,
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: t.color.bgSub,
                                },
                                '& .MuiDataGrid-columnSeparator': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: TEXT_COLOR,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                },
                                '& .MuiDataGrid-cell': {
                                    fontSize: 13,
                                    color: t.color.fg,
                                    borderBottom: `1px solid ${t.color.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: t.color.bgSub,
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    borderTop: `1px solid ${t.color.divider}`,
                                },
                                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                    outline: 'none',
                                },
                            }}
                        />
                    </Card>
                </Container>
            </Box>
        </ThemeProvider>
    );
}
